/* ROLL DICE — the individual die faces behind a Roll20 chat total.
 *
 * A Beyond20/Roll20 roll-template card gives us a row's TOTAL (damageFromCard &c.),
 * but some rules read the dice THEMSELVES, not the sum: Chromatic Orb leaps when two
 * of its d8s match (state/chromaticOrb.ts), a crit is a die that rolled its max, a
 * brutal-critical adds a die. The sum cannot answer any of those.
 *
 * Roll20 keeps every inline roll's breakdown in the result element's `original-title`
 * tooltip — the connector relays it verbatim as the row's `tip` (connector/roll20.js).
 * Its modern HTML form, walking left to right, is:
 *
 *     Rolling 3d8 = (<span class="basicdiceroll">3</span>+<span class="basicdiceroll">7</span>+<span class="basicdiceroll critsuccess">8</span>)
 *
 *   · each `d(\d+)` token sets the current die SIZE (the M in NdM);
 *   · each `<span class="…diceroll…">(\d+)</span>` is one die of the current size;
 *   · a span whose class contains `dropped` was removed by keep/drop (4d6 drop lowest,
 *     advantage) and must NOT count as a live die;
 *   · `critsuccess` / `critfail` mark a natural max / 1 — kept here for later, not needed
 *     to know a face.
 *
 * Pure string → structure, so it unit-tests in Node without a browser (test-roll-dice.mjs)
 * and the real Roll20 markup is the acceptance test — capture one live card and the tests
 * pin whatever it actually emits. This file deliberately does NOT decide anything about a
 * spell; it only reports the dice. */

/** One die from a roll: the size of its die (M in NdM), the face it showed, and whether
 *  Roll20 dropped it (so a keep/drop or advantage roll's discarded dice don't count). */
export interface Die { size: number; value: number; dropped: boolean }

/** The kept faces of a given die size — what a bounce or crit check reads. Dropped dice
 *  and other die sizes are excluded, so a mixed roll (Ice Storm's 2d8+4d6) yields only the
 *  d8s when asked for 8. */
export const facesOfSize = (dice: Die[], size: number): number[] =>
  dice.filter((d) => d.size === size && !d.dropped).map((d) => d.value);

/** Parse a Roll20 inline-roll tooltip (the row's `tip`) into its individual dice.
 *  See the file header for the tooltip's shape and the rules for what counts. A tip that
 *  is empty, a flat typed number, or markup this does not recognise yields `[]` — absent
 *  dice are unknown, never a silent zero (the detectors treat an empty list as "no reading",
 *  not "no matches"). */
export function diceFromTip(tip: string): Die[] {
  const t = String(tip || "");
  // The formula's die terms, in order: "2d8+4d6" -> [{n:2,size:8},{n:4,size:6}]. Roll20
  // lists every die of a term together and in this order, so the Nth run of dice spans
  // belongs to the Nth term — which is how a mixed roll keeps each size straight without
  // having to parse the parenthesised groups. A term's `n` is its base count (keep/drop
  // notation like 4d6d1 still shows all 4 dice, one flagged dropped), so the counts line up.
  const terms: { n: number; size: number }[] = [];
  for (const m of t.matchAll(/(\d+)d(\d+)/gi)) terms.push({ n: Number(m[1]), size: Number(m[2]) });
  const dice: Die[] = [];
  let ti = 0, used = 0;
  for (const m of t.matchAll(/<span class="([^"]*)"[^>]*>(\d+)<\/span>/gi)) {
    const cls = m[1];
    if (!/diceroll/i.test(cls)) continue;                               // a die span, not other tooltip markup
    while (ti < terms.length && used >= terms[ti].n) { ti++; used = 0; } // advance to the term this die belongs to
    if (ti >= terms.length) break;                                      // more dice than the formula declared — stop, don't guess a size
    dice.push({ size: terms[ti].size, value: Number(m[2]), dropped: /\bdropped\b/i.test(cls) });
    used++;
  }
  return dice;
}
